/* eslint-disable @typescript-eslint/no-empty-interface */
import { Injectable } from '@angular/core';
import { JSONRPCService } from '@s8l/client-tree-lib';

import { serviceURL } from './service';

export type Instant = any;
export type Duration = any;
export type SecondFactor = 'none' | 'mail';
export type LicenceUsage = 'registration' | 'empowerment' | 'unrestricted';

export interface Role {
  name?: string;
  description?: string;
  created?: Date;
  updated?: Date;
}

export interface Profile {
  uuid?: string;
  username?: string;
  meta?: Metadata;
  settings?: any;
  labels?: any;
  enabled?: boolean;
}

export interface Metadata {
  mail?: string;
  roles?: string[];
  confirmation?: SecondFactor;
  licences?: LicenceActivation[];
  expiration?: Instant;
  registration?: Instant; // Only for profiles
  duration?: Duration; // Only for templates
}

export interface LicenceActivation {
  key?: string;
  active?: boolean;
  roles?: string[];
  activation?: Instant;
  expiration?: Instant;
  duration?: Duration;
}

export interface Template {
  uuid?: string;
  username?: string;
  pattern?: string;
  precedence: number;
  profile: TemplateProfile;
  created?: Instant;
  updated?: Instant;
}

export interface Licence extends Template {
  key?: string;
  volume?: number;
  activations?: number;
  usage?: LicenceUsage;
  description?: string;
}

export interface TemplateProfile {
  meta?: Metadata;
  settings?: any;
  labels?: any;
  enabled?: boolean;
}

export interface RoleUpsertRequest {
  name?: string;
  description?: string;
}

export interface LicenceActivateRequest {
  user?: string;
  licence?: string;
}

export interface AuditFetchRequest {
  username?: string;
  action?: string;
  resource?: string;
  value?: string;
  api?: boolean;
  unprivileged?: boolean;
  offset?: number;
  limit?: number;
}

export interface UserAuditRecord {
  uuid?: string;
  username?: string;
  resource?: string;
  action?: string;
  value?: any;
  timestamp?: Instant;
}

export interface LoginRequest {
  app?: string;
  username?: string;
  password?: string;
  device?: LoginDevice;
  token?: string;
}

export interface LoginDevice {
  id?: string;
  platform?: string;
}

export interface LoginResponse {
  token?: string;
  profile?: Profile;
}

export interface PasswordRule {
  pattern?: string;
  match?: boolean;
  error?: PasswordError;
}

export interface PasswordError {
  code?: number;
  message?: string;
  data?: any;
}

export interface UuidRequest {
  uuid?: string;
}

export interface RoleRequest {
  name?: string;
}

export interface UserUpsertRequest {
  password?: string;
  profile?: Profile;
  updateUsername?: boolean;
}

export interface PermissionUpsertRequest {
  role: string;
  resource?: string;
  permission: string | string[];
}

export interface PermissionLegacyUpsertRequest {
  role: string;
  permission: string | string[];
}

// ---

@Injectable({
  providedIn: 'root'
})
export class UserServerService extends JSONRPCService {
  // Personal account

  public async accountLogin(params: LoginRequest): Promise<LoginResponse> {
    return this.message(serviceURL('USER_SERVER', '/account'), 'login', params);
  }

  public async accountRefresh(): Promise<LoginResponse> {
    return this.message(serviceURL('USER_SERVER', '/account'), 'refresh', { app: 'cms' });
  }

  public async accountLogout(): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/account'), 'logout', null);
  }

  public async accountAlive(): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/account'), 'alive', null);
  }

  public async profileGet(): Promise<Profile> {
    return this.message(serviceURL('USER_SERVER', '/profile'), 'get', null);
  }

  // Password rules

  public async accountPasswordRules(): Promise<PasswordRule[]> {
    return this.message(serviceURL('USER_SERVER', '/account'), 'password-rules', null);
  }

  // Roles

  public async roleList(): Promise<Role[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/role'), 'list', null);
  }

  public async roleCreate(params: RoleUpsertRequest): Promise<Role> {
    return this.message(serviceURL('USER_SERVER', '/admin/role'), 'create', params);
  }

  public async roleUpdate(params: RoleUpsertRequest): Promise<Role> {
    return this.message(serviceURL('USER_SERVER', '/admin/role'), 'update', params);
  }

  public async roleDelete(params: RoleRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/role'), 'delete', params);
  }

  public async roleUsers(params: RoleRequest): Promise<string[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/role'), 'users', params);
  }

  // Permissions

  public async permissionSubjects(): Promise<string[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'subjects', null);
  }

  public async permissionSet(params: PermissionUpsertRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'set', params);
  }

  public async permissionAdd(params: PermissionUpsertRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'add', params);
  }

  public async permissionRemove(params: PermissionUpsertRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'remove', params);
  }

  public async permissionLegacyCreate(params: PermissionLegacyUpsertRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'create', params);
  }

  public async permissionLegacyDelete(params: PermissionLegacyUpsertRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/permission'), 'delete', params);
  }

  // User Profiles

  public async userList(): Promise<Profile[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/user'), 'list', null);
  }

  public async userCreate(params: UserUpsertRequest): Promise<Profile> {
    return this.message(serviceURL('USER_SERVER', '/admin/user'), 'create', params);
  }

  public async userUpdate(params: UserUpsertRequest): Promise<Profile> {
    return this.message(serviceURL('USER_SERVER', '/admin/user'), 'update', params);
  }

  public async userDelete(params: UuidRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/user'), 'delete', params);
  }

  public async userActivate(params: LicenceActivateRequest): Promise<Profile> {
    return this.message(serviceURL('USER_SERVER', '/admin/user/licence'), 'activate', params);
  }

  // Templates & Licences

  public async templateList(): Promise<Template[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/template'), 'list', null);
  }

  public async templateCreate(params: Template): Promise<Template> {
    return this.message(serviceURL('USER_SERVER', '/admin/template'), 'create', params);
  }

  public async templateUpdate(params: Template): Promise<Template> {
    return this.message(serviceURL('USER_SERVER', '/admin/template'), 'update', params);
  }

  public async templateDelete(params: UuidRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/template'), 'delete', params);
  }

  public async licenceCreate(params: Licence): Promise<Licence> {
    return this.message(serviceURL('USER_SERVER', '/admin/template/licence'), 'create', params);
  }

  public async licenceUpdate(params: Licence): Promise<Licence> {
    return this.message(serviceURL('USER_SERVER', '/admin/template/licence'), 'update', params);
  }

  public async licenceDelete(params: UuidRequest): Promise<void> {
    return this.message(serviceURL('USER_SERVER', '/admin/template/licence'), 'delete', params);
  }

  // Audit Logs

  public async auditFetch(params: AuditFetchRequest): Promise<UserAuditRecord[]> {
    return this.message(serviceURL('USER_SERVER', '/admin/audit'), 'fetch', params);
  }
}
